import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type { RootState } from "store";
import { createPayGCCPaymentThunk } from "slices/paygcc/thunk";
import DashboardHeader from "../../Components/Common/DashboardHeader";

interface CartItem {
  id: number;
  course_name: string;
  thumbnail_url: string;
  noofweek: string;
  hourperday: string;
  course_level: string;
  price: number;
  originalPrice: number;
  discount: number;
  quantity?: number;
}

const ShoppingCart = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Get cart items from sessionStorage (authenticated cart)
    const savedCart = sessionStorage.getItem("cartItems");
    if (savedCart) {
      try {
        const items = JSON.parse(savedCart);
        setCartItems(items);
      } catch (error) {
        console.error("Error parsing cart items:", error);
      }
    }
  }, []);

  const removeFromCart = (id: number) => {
    const updatedCart = cartItems.filter(item => item.id !== id);
    setCartItems(updatedCart);
    sessionStorage.setItem("cartItems", JSON.stringify(updatedCart));
    // Dispatch custom event to update cart icon
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const updateQuantity = (id: number, newQuantity: number) => {
    if (newQuantity < 1) {
      removeFromCart(id);
      return;
    }
    const updatedCart = cartItems.map(item => 
      item.id === id 
        ? { ...item, quantity: newQuantity }
        : item
    );
    setCartItems(updatedCart);
    sessionStorage.setItem("cartItems", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const increaseQuantity = (id: number) => {
    const item = cartItems.find(item => item.id === id);
    if (item) {
      const currentQuantity = item.quantity || 1;
      updateQuantity(id, currentQuantity + 1);
    }
  };

  const decreaseQuantity = (id: number) => {
    const item = cartItems.find(item => item.id === id);
    if (item) {
      const currentQuantity = item.quantity || 1;
      updateQuantity(id, currentQuantity - 1);
    }
  };

  const calculateTotals = () => {
    const courseFee = cartItems.reduce((sum, item) => {
      const quantity = item.quantity || 1;
      return sum + (item.price * quantity);
    }, 0);
    const tax = courseFee * 0.10; 
    const discount = 0; 
    const total = courseFee + tax - discount;
    
    return {
      courseFee: courseFee.toFixed(2),
      tax: tax.toFixed(2),
      discount: discount.toFixed(2),
      total: total.toFixed(2),
      courseFeeNum: courseFee,
      taxNum: tax,
      discountNum: discount,
      totalNum: total
    };
  };

  const totals = calculateTotals();

  const clearCart = () => {
    setCartItems([]);
    sessionStorage.removeItem("cartItems");
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const handleProceedToCheckout = async () => {
    if (!termsAccepted) {
      toast.error("Please accept the terms & conditions");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setIsProcessing(true);

    try {
      // Build the payload from cart items
      const firstItem = cartItems[0];
      
      // Calculate total quantity (sum of all item quantities)
      const totalQuantity = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
      
      // Calculate values
      const subTotal = parseFloat(totals.courseFee);
      const discount = parseFloat(totals.discount);
      const vat = parseFloat(totals.tax);
      const total = parseFloat(totals.total);
      
      // For unit_price, use average price per item or total divided by total quantity
      const unitPrice = totalQuantity > 0 ? total / totalQuantity : total;
      const amount = total;

      // Build product name from all courses
      const productNames = cartItems.map(item => item.course_name).join(", ");
      const description = cartItems.length === 1 
        ? `${firstItem.course_name} - ${firstItem.course_level} level course`
        : `${totalQuantity} courses: ${productNames}`;

      const payload = {
        gatwayname: "Pay GCC",
        servicename: "AI Tutorial",
        product_name: cartItems.length === 1 ? firstItem.course_name : productNames,
        description: description,
        quantity: totalQuantity,
        unit_price: Math.round(unitPrice * 100) / 100, // Round to 2 decimal places
        amount: Math.round(amount * 100) / 100,
        sub_total: Math.round(subTotal * 100) / 100,
        discount: Math.round(discount * 100) / 100,
        vat: Math.round(vat * 100) / 100,
        total: Math.round(total * 100) / 100
      };

      const result: any = await dispatch(createPayGCCPaymentThunk(payload) as any);

      if (result?.success && result?.data?.payment_url) {
        toast.success("Redirecting to payment gateway...");
        // Redirect to payment URL
        clearCart();
        window.location.href = result.data.payment_url;
      } else {
        toast.error(result?.message || "Failed to create payment. Please try again.");
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast.error("An error occurred during checkout. Please try again.");
      setIsProcessing(false);
    }
  };

  document.title = "Shopping Cart - Amanat";

  return (
    <div className="shopping-cart-page">
      <DashboardHeader />

      {/* Main Content */}
      <main className="cart-main">
        <div className="container">
          <div className="cart-layout">
            {/* Left Side - Cart Items */}
            <div className="cart-items-section">
              <h1 className="cart-heading">Shopping Cart</h1>
              <p className="cart-subtitle">{cartItems.length} {cartItems.length === 1 ? 'Course' : 'Courses'} in Cart</p>

              {cartItems.length === 0 ? (
                <div className="empty-cart">
                  <p>Your cart is empty</p>
                  <Link to="/explore-courses">
                    <Button className="browse-btn">Browse Courses</Button>
                  </Link>
                </div>
              ) : (
                <div className="cart-items-list">
                  {cartItems.map((item) => {
                    const itemDiscount = item.originalPrice > item.price 
                      ? Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)
                      : 0;
                    const quantity = item.quantity || 1;
                    const itemTotal = item.price * quantity;

                    return (
                      <div key={item.id} className="cart-item-card">
                        <div className="item-thumbnail">
                          <img 
                            src={item.thumbnail_url || "https://via.placeholder.com/150x100"} 
                            alt={item.course_name}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "https://via.placeholder.com/150x100";
                            }}
                          />
                        </div>
                        <div className="item-details">
                          <h3 className="item-title">{item.course_name}</h3>
                          <div className="item-info">
                            <span>{item.noofweek || "12"} week</span>
                            <span className="separator">|</span>
                            <span>{item.hourperday || "5"} hrs/day</span>
                            <span className="separator">|</span>
                            <span>{item.course_level || "Beginner"} level</span>
                          </div>
                          <div className="item-pricing">
                            {item.originalPrice > item.price && (
                              <>
                                <span className="original-price">BD {item.originalPrice.toFixed(0)}</span>
                                <span className="discount-badge">{itemDiscount}% off</span>
                              </>
                            )}
                            <span className="current-price">BD {itemTotal.toFixed(0)}</span>
                          </div>
                        </div>
                        <div className="item-actions">
                          <button 
                            className="remove-btn"
                            onClick={() => removeFromCart(item.id)}
                          >
                            REMOVE
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right Side - Price Details */}
            {cartItems.length > 0 && (
              <div className="price-details-section">
                <div className="price-details-card">
                  <h2 className="price-title">Price Details</h2>
                  <div className="price-breakdown">
                    <div className="price-row">
                      <span className="price-label">Course fee</span>
                      <span className="price-value">BD {totals.courseFee}</span>
                    </div>
                    <div className="price-row">
                      <span className="price-label">VAT</span>
                      <span className="price-value">BD {totals.tax}</span>
                    </div>
                    <div className="price-row discount-row">
                      <span className="price-label">Discount</span>
                      <span className="price-value discount-value">-BD {totals.discount}</span>
                    </div>
                    <div className="price-divider"></div>
                    <div className="price-row total-row">
                      <span className="price-label">Total</span>
                      <span className="price-value total-value">BD {totals.total}</span>
                    </div>
                  </div>
                  <div className="terms-checkbox">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <label htmlFor="terms">
                      <Link
                        to="/terms-and-conditions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms &amp; Conditions
                      </Link>
                      {" "}and{" "}
                      <Link
                        to="/refund-policy"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Refund Policy
                      </Link>
                    </label>
                  </div>
                  <Button 
                    className="checkout-btn"
                    disabled={!termsAccepted || isProcessing}
                    onClick={handleProceedToCheckout}
                  >
                    {isProcessing ? (
                      <>
                        <i className="ri-loader-4-line spin-icon"></i>
                        Processing...
                      </>
                    ) : (
                      <>
                        Proceed to Checkout
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ShoppingCart;

